import { Observable, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';

import { QuestSet } from '../firestore-models/quest-set';
import { ProjectLanguage } from '../firestore-models/project-language';
import { ApiQuestion, ApiQuestSet, ApiSetQuestSetRequest } from '../api-client-module';

import { ApiClientService } from '../api-client.service';
import { ConfigurationService } from '../configuration.service';
import { SpinnerService } from '../services/spinner.service';
import { AppConst } from '../app-const';

import { QuestSetDialogComponent } from './quest-set-dialog/quest-set-dialog.component';
import { MessageDialogComponent } from '../common/message-dialog/message-dialog.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-quest-set',
  templateUrl: './quest-set.component.html',
  styleUrls: ['./quest-set.component.css'],
  standalone: false,
})
export class QuestSetComponent implements OnInit {

  // Questionコレクション
  private questSetCollection!: AngularFirestoreCollection<QuestSet>;
  questSetObservable!: Observable<QuestSet[]>;
  questSetSubscribe!: Subscription;
  selection = new SelectionModel<QuestSet>(true, []);

  // 各フロア情報に紐付くプロジェクトID
  projectId: string;
  // 各フロア情報に紐付くプロジェクトの言語情報
  projectLanguages!: Array<ProjectLanguage>;

  // フロア情報テーブル
  displayedColumns: string[] = ['position', 'edit', 'select', 'id', 'use_question', 'question_title', 'name', 'questions', 'date'];
  dataSource = new MatTableDataSource<QuestSet>();

  projectBaseLang: string = 'ja';

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  constructor(
    public dialog: MatDialog,
    private angularFirestore: AngularFirestore,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private configurationService: ConfigurationService,
    private translate: TranslateService
  ) {
    this.projectId = this.configurationService.getProjectId();
  }

  ngOnInit(): void {
    this.projectLanguages = this.configurationService.getProject().languages!;
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.questSetCollection = this.angularFirestore.collection<QuestSet>(
      AppConst.FIRESTORE_QUEST_SET, ref => ref.where('project_id', '==', this.projectId).orderBy('created', 'desc'));
    this.questSetObservable = this.questSetCollection.valueChanges({ idField: 'id' });
    this.questSetSubscribe = this.questSetObservable.subscribe(quest_set => {
      this.dataSource.data = this.addFilterValue(quest_set);
    });
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
  }

  ngOnDestroy(): void {
    this.questSetSubscribe.unsubscribe();
  }

  // 追加ダイアログ
  openAddDialog(): void {
    const dialogRef = this.dialog.open(QuestSetDialogComponent, {
      width: '85%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_ADD, quest_set: null }
    });
  }

  // 編集ダイアログ
  openEditDialog(quest_set: QuestSet): void {
    let questions: Array<ApiQuestion> = new Array<ApiQuestion>();
    for (const question of quest_set.questions) {
      const question_check_date_range_upper: any = question.question_check_date_range_upper ? new Date(question!.question_check_date_range_upper.seconds * 1000) : null;
      const question_check_date_range_lower: any = question.question_check_date_range_lower ? new Date(question!.question_check_date_range_lower.seconds * 1000) : null;
      const question_check_base_date: any = question.question_check_base_date ? new Date(question!.question_check_base_date.seconds * 1000) : null;
      let apiQuestion: ApiQuestion = {
        question_id: question.question_id,
        display_no: question.display_no,
        question_names: question.question_names,
        question_comps: question.question_comps,
        question_type: question.question_type,
        question_select_items: question.question_select_items,
        question_check: question.question_check,
        question_check_range_upper: question.question_check_range_upper,
        question_check_range_lower: question.question_check_range_lower,
        question_check_date_range_upper: question_check_date_range_upper,
        question_check_date_range_lower: question_check_date_range_lower,
        question_check_base_date: question_check_base_date,
        question_check_base_date_to_start: question.question_check_base_date_to_start,
        question_check_base_date_to_end: question.question_check_base_date_to_end,
        question_check_col_count_upper: question.question_check_col_count_upper,
        question_check_col_count_lower: question.question_check_col_count_lower,
        project_id: question.project_id,
        enabled: question.enabled,
      };
      questions.push(apiQuestion);
    }
    let apiQuestSet: ApiQuestSet = {
      id: quest_set.id,
      name: quest_set.name,
      question_titles: quest_set.question_titles,
      disp_order: quest_set.disp_order,
      use_question: quest_set.use_question,
      questions: questions,
      dl_files: quest_set.dl_files,
      project_id: quest_set.project_id,
      enabled: quest_set.enabled,
    };
    const dialogRef = this.dialog.open(QuestSetDialogComponent, {
      width: '85%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_UPDATE, quest_set: apiQuestSet }
    });
 }
  
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
    this.selection.clear() :
    this.dataSource.data.forEach(row => this.selection.select(row));
  }

  checkboxLabel(row?: QuestSet): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'}`;
  }

  // 削除
  deleteData(): void{
    const dialogRef = this.dialog.open(MessageDialogComponent, {
      width: '420px',
      height: '180px',
      disableClose: true,
      data: { message: this.translate.instant('選択されたデータを削除します。よろしいですか？') }
    });

    dialogRef.afterClosed().subscribe(result => {
      let selectedQuestsets: Array<ApiQuestSet> = new Array<ApiQuestSet>();
      for (const row of this.dataSource.data) {
        if (this.selection.isSelected(row)) {
          let questions: Array<ApiQuestion> = new Array<ApiQuestion>();
          for( const question of row.questions) {
            const question_check_date_range_upper: any = question.question_check_date_range_upper ? new Date(question!.question_check_date_range_upper.seconds * 1000) : null;
            const question_check_date_range_lower: any = question.question_check_date_range_lower ? new Date(question!.question_check_date_range_lower.seconds * 1000) : null;
            const question_check_base_date: any = question.question_check_base_date ? new Date(question!.question_check_base_date.seconds * 1000) : null;
            let apiQuestion: ApiQuestion = {
              question_id: question.question_id,
              display_no: question.display_no,
              question_names: question.question_names,
              question_comps: question.question_comps,
              question_type: question.question_type,
              question_select_items: question.question_select_items,
              question_check: question.question_check,
              question_check_range_upper: question.question_check_range_upper,
              question_check_range_lower: question.question_check_range_lower,
              question_check_date_range_upper: question_check_date_range_upper,
              question_check_date_range_lower: question_check_date_range_lower,
              question_check_base_date: question_check_base_date,
              question_check_base_date_to_start: question.question_check_base_date_to_start,
              question_check_base_date_to_end: question.question_check_base_date_to_end,
              question_check_col_count_upper: question.question_check_col_count_upper,
              question_check_col_count_lower: question.question_check_col_count_lower,
              project_id: question.project_id,
              enabled: question.enabled,
            };
            questions.push(apiQuestion);
          }
      
          const selectedQuestSet: ApiQuestSet = {
            id: row.id,
            name: row.name,
            question_titles: row.question_titles,
            questions: questions,
            project_id: row.project_id,
            enabled: row.enabled,
          };
          selectedQuestsets.push(selectedQuestSet);
        }
      }
      if (!!result) {
        let request: ApiSetQuestSetRequest = {
          access_token: '',
          change_type: AppConst.DATA_CHANGE_TYPE_DELETE,
          quest_set: selectedQuestsets,
          project_id: this.projectId,
        }
        this.spinnerService.openSpinner();
        this.apiClientService.setQuestSet(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('削除しました。'));
            } else {
              if (response!.ret === AppConst.API_RET_IN_USE_DATA) {
                this.toastr.error(this.translate.instant('削除できませんでした。フロア・表示グループ情報で利用中のデータが含まれている可能性があります。'));
              } else {
                this.toastr.error(this.translate.instant('削除できませんでした。'));
              }
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('削除できませんでした。'));
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
            this.selection.clear();
          }
        );
      }
    });
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'id':
          return compare(a.id!, b.id!, isAsc);
        case 'name':
          return compare(a.name!, b.name!, isAsc);
        case 'date':
          return compare(a.created.toMillis(), b.created.toMillis(), isAsc);
        default:
          return 0;
      }
    });
  }

  addFilterValue(docs: Array<any>): Array<any> {
    for (const doc of docs) {
      let filterValue = '';
      if (doc.question_titles) {
        for (const value of doc.question_titles) {
          filterValue += value.string_message + ' '
        }
      }
      filterValue += doc.name + ' ';
      doc['filterValue'] = filterValue;
    }
    return docs;
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

}
